/**
 * What happens to a role's confirmed lock when its assignees change.
 *
 * Kept free of Supabase and other app modules so pure helpers (and their
 * tests) can use it without initialising a database client.
 */
import type { Assignment, AssignmentEmployee } from "@/hooks/useProjectBoardData";

/**
 * Whether a role with this many assignees can carry the confirmed lock. The lock
 * confirms one specific person, so it only exists on a single-assignee role
 * (AssignmentRow hides the lock control otherwise).
 */
export function canBeConfirmed(employeeCount: number): boolean {
  return employeeCount === 1;
}

/**
 * Whether a role keeps its confirmed lock when its employee set changes.
 *
 * Only a one-for-one swap keeps it: one person was assigned before and one
 * (different) person after. Unconfirming is a separate choice made with the
 * lock control, never a side effect of swapping who is assigned.
 *
 * Any other change ends somewhere the lock can't exist or didn't before:
 * clearing the role, staffing a role that was empty, or ending with two or
 * more people. Those always leave the role unconfirmed; otherwise a lock with
 * no visible control would linger and carry over to the next assignment.
 */
export function staysConfirmed(
  wasConfirmed: boolean | null | undefined,
  oldEmployeeCount: number,
  newEmployeeCount: number,
): boolean {
  return (
    Boolean(wasConfirmed) &&
    canBeConfirmed(oldEmployeeCount) &&
    canBeConfirmed(newEmployeeCount)
  );
}

/**
 * Whether a change to the employee set must write is_confirmed: false. Every
 * change except a one-for-one swap does, regardless of the client's view of
 * the flag, which can be stale (another user may have locked the role since it
 * was loaded).
 */
export function mustUnconfirm(oldEmployeeCount: number, newEmployeeCount: number): boolean {
  return !(canBeConfirmed(oldEmployeeCount) && canBeConfirmed(newEmployeeCount));
}

/**
 * The board copy of an assignment after its employee set was written.
 *
 * Pass `persistedIsConfirmed` (the row the save returned) whenever it is known:
 * a one-for-one swap leaves the column alone, so the database keeps its own
 * current value, which this client's copy may not match if someone else
 * changed the lock meanwhile. Without it, the lock follows the same rule as
 * the database write, applied to this client's copy.
 */
export function withWrittenEmployees<T extends Assignment>(
  assignment: T,
  employees: AssignmentEmployee[],
  persistedIsConfirmed?: boolean | null,
): T {
  if (persistedIsConfirmed !== undefined) {
    return { ...assignment, employees, is_confirmed: persistedIsConfirmed };
  }
  return {
    ...assignment,
    employees,
    is_confirmed: staysConfirmed(
      assignment.is_confirmed,
      assignment.employees.length,
      employees.length,
    )
      ? assignment.is_confirmed
      : false,
  };
}
